import type { BillingSummaryDto } from '@lib/dto/billing.dto'
import { cardBrandLabel } from '@lib/helpers/card-brand'
import { formatCents, formatMicros } from '@lib/helpers/money'
import { Badge, type BadgeTone } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

const STATUS: Record<BillingSummaryDto['autoReload']['status'], { label: string; tone: BadgeTone; action: string }> = {
  off: { label: 'Off', tone: 'neutral', action: 'Set up' },
  active: { label: 'On', tone: 'success', action: 'Edit' },
  paused: { label: 'Paused', tone: 'warning', action: 'Update card' },
}

interface AutoReloadCardProps {
  summary: BillingSummaryDto
  /** Opens the settings, for an Owner. */
  onEdit: (() => void) | null
}

/**
 * Auto-reload at a glance: whether it's on, its numbers, how much it's added
 * this month, the card it charges, and why it paused. Every member sees it;
 * an organization PixelSign doesn't bill has nothing to reload.
 */
export function AutoReloadCard({ summary, onEdit }: AutoReloadCardProps) {
  if (summary.exempt) return null
  const reload = summary.autoReload
  const status = STATUS[reload.status]
  const card = summary.card

  function describe() {
    if (reload.status === 'off') {
      return <p className="text-body text-muted-foreground">Add credit automatically when the balance runs low, so live sends aren’t refused.</p>
    }
    return (
      <p className="text-body tabular-nums">
        When the balance falls below {formatMicros(reload.triggerMicros)}, add {formatCents(reload.amountCents)} (plus tax), up to{' '}
        {formatCents(reload.monthlyCapCents)} a month. {formatCents(reload.reloadedThisMonthCents)} reloaded this month.
      </p>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Auto-reload
          <Badge tone={status.tone}>{status.label}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {describe()}
        {card ? (
          <p className="text-caption text-muted-foreground tabular-nums">
            {cardBrandLabel(card.brand)} •••• {card.last4}, expires {String(card.expMonth).padStart(2, '0')}/{String(card.expYear % 100).padStart(2, '0')}
          </p>
        ) : null}
        {reload.status === 'paused' ? <p className="text-body text-danger">Paused: {reload.pausedReason ?? 'the last charge failed.'}</p> : null}
        {onEdit ? (
          <Button variant="secondary" size="sm" onClick={onEdit}>
            {status.action}
          </Button>
        ) : null}
      </CardContent>
    </Card>
  )
}
